import { useEffect, useState } from "react";
import { MessageSquare, Trash2 } from "lucide-react";
import { Button, Field, Modal, Note, inputClass } from "@/components/portal/Ui";
import { formatDateTime } from "@/lib/format";

/** Internal-only remark on a row. Never shown to creators, clients or exports. */
export function RemarkCell({ value, byName, at, onSave, testId }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setText(value || "");
  }, [open, value]);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    const ok = await onSave(text.trim());
    setBusy(false);
    if (ok !== false) setOpen(false);
  };

  return (
    <>
      {value ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`${testId}-open`}
          title={value}
          className="flex max-w-[190px] items-start gap-1.5 text-left text-xs font-semibold text-[#8A5A00] hover:underline"
        >
          <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="line-clamp-2">{value}</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`${testId}-open`}
          className="text-left text-xs font-semibold text-[#6b6660] hover:text-[#111111] hover:underline"
        >
          Add remark
        </button>
      )}

      <Modal
        open={open}
        onClose={() => !busy && setOpen(false)}
        title="Internal remark"
        testId={`${testId}-modal`}
      >
        <form onSubmit={save} className="space-y-4">
          <Note tone="neutral">
            Internal team only. Creators, clients, report exports and the invoice PDF never show
            this.
          </Note>
          <Field label="Remark" hint="Leave it empty to clear the remark.">
            <textarea
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className={inputClass}
              placeholder="e.g. Client asked us to hold payment until the reel is reposted."
              data-testid={`${testId}-input`}
            />
          </Field>
          {byName && (
            <p className="text-xs body-text" data-testid={`${testId}-meta`}>
              Last edited by {byName} · {formatDateTime(at)}
            </p>
          )}
          <div className="flex justify-end gap-2.5">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} testId={`${testId}-cancel`}>
              Cancel
            </Button>
            <Button type="submit" busy={busy} testId={`${testId}-save`}>
              Save remark
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}

/** Admin delete with a named target and a mandatory reason. */
export function DeleteConfirmModal({ open, onClose, title, summary, warning, onConfirm, testId }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    const ok = await onConfirm(reason.trim());
    setBusy(false);
    if (ok !== false) onClose();
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title={title} testId={testId}>
      <form onSubmit={submit} className="space-y-4">
        <Note tone="red" testId={`${testId}-summary`}>
          {summary}
        </Note>
        {warning && <p className="text-sm leading-relaxed body-text">{warning}</p>}
        <Field label="Reason for deleting" required hint="Written to the activity log.">
          <input
            required
            minLength={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={inputClass}
            placeholder="e.g. duplicate submission from the KOL"
            data-testid={`${testId}-reason`}
          />
        </Field>
        <div className="flex justify-end gap-2.5">
          <Button variant="outline" onClick={onClose} disabled={busy} testId={`${testId}-cancel`}>
            Keep it
          </Button>
          <Button type="submit" variant="danger" busy={busy} testId={`${testId}-confirm`}>
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </div>
      </form>
    </Modal>
  );
}
